import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { Navigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import ModelloFatturaEditor from "../components/ModelloFatturaEditor";
import {
  listModelli,
  eliminaModello,
  duplicaModello,
  syncFicModelli,
} from "../utils/modelliFatturaApi";
import {
  TIPO_MODELLO_LABELS,
  TIPO_MODELLO_COLORS,
} from "../utils/variabiliFattura";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Search,
  MoreVertical,
  Eye,
  Pencil,
  Copy,
  Trash2,
  Loader2,
  FileText,
  Cloud,
  Layout,
  Sparkles,
  Info,
  ExternalLink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function InfoCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-xs text-slate-500 leading-relaxed">{children}</p>
    </div>
  );
}

const TIPO_OPTIONS = [
  { value: "", label: "Tutti i tipi" },
  { value: "retta", label: "Retta" },
  { value: "servizi", label: "Servizi" },
  { value: "acconto", label: "Acconto" },
  { value: "saldo", label: "Saldo" },
  { value: "dimissione", label: "Dimissione" },
  { value: "altro", label: "Altro" },
];

export default function ModelliFattura({ embedded = false }) {
  const { user, token, activeStruttura } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("miei");
  const [modelli, setModelli] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingModello, setEditingModello] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canAccess = canAccessFatturazioneElettronica(user);

  const loadModelli = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await listModelli(token, {
        tab,
        q: search || undefined,
        tipo: tipoFilter || undefined,
        page,
        perPage,
      });
      setModelli(data.modelli || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento dei modelli");
    } finally {
      setLoading(false);
    }
  }, [token, tab, search, tipoFilter, page, perPage, activeStruttura?.id]);

  useEffect(() => {
    loadModelli();
  }, [loadModelli]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncFicModelli(token);
      toast.success(res.message || "Sincronizzazione completata");
      loadModelli();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore sincronizzazione Fatture in Cloud");
    } finally {
      setSyncing(false);
    }
  };

  const handleDuplica = async (modello) => {
    try {
      await duplicaModello(token, modello.id);
      toast.success(`Modello "${modello.nome}" duplicato`);
      loadModelli();
    } catch {
      toast.error("Errore durante la duplicazione");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eliminaModello(token, deleteTarget.id);
      toast.success(`Modello "${deleteTarget.nome}" eliminato`);
      setDeleteTarget(null);
      loadModelli();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditorSave = () => {
    setEditorOpen(false);
    setEditingModello(null);
    loadModelli();
  };

  const totalPages = Math.ceil(total / perPage);

  if (!canAccess) return <Navigate to="/fatturazione" replace />;

  return (
    <div className={embedded ? "space-y-3" : "p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6"}>
      {!embedded && (
        <PageHeader
          title="Modelli di fattura"
          subtitle="Gestisci i modelli utilizzati per creare le fatture"
          breadcrumb={[
            { label: "Fatturazione", href: "/fatturazione" },
            { label: "Modelli di fattura" },
          ]}
        />
      )}

      <div className={embedded ? "space-y-3" : "flex flex-col lg:flex-row gap-6"}>
        <div className="flex-1 min-w-0">
          <Card>
            <CardContent className="p-0">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
                <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
                  <TabsList className="h-9">
                    <TabsTrigger value="miei" className="text-xs px-3">
                      <Layout className="w-3.5 h-3.5 mr-1.5" />
                      I miei modelli
                    </TabsTrigger>
                    <TabsTrigger value="fic" className="text-xs px-3">
                      <Cloud className="w-3.5 h-3.5 mr-1.5" />
                      Modelli di Fatture in Cloud
                      <Badge className="ml-1.5 text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-0">
                        Beta
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSync}
                    disabled={syncing}
                    className="text-xs"
                  >
                    {syncing ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Sincronizza modelli
                  </Button>
                  <Button
                    size="sm"
                    className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                    onClick={() => { setEditingModello(null); setEditorOpen(true); }}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Nuovo modello
                  </Button>
                </div>
              </div>

              {/* Filtri */}
              <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    className="pl-8 h-8 text-xs"
                    placeholder="Cerca modello..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  />
                </div>
                <Select value={tipoFilter} onValueChange={(v) => { setTipoFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="h-8 text-xs w-40">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_OPTIONS.map((o) => (
                      <SelectItem key={o.value || "all"} value={o.value || "all"} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tabella */}
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-[#4CAF50]" />
                </div>
              ) : modelli.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
                  <FileText className="w-10 h-10 opacity-30" />
                  <p className="text-sm">
                    {tab === "fic"
                      ? "Nessun modello FIC sincronizzato. Clicca 'Sincronizza modelli'."
                      : "Nessun modello trovato. Crea il primo modello personalizzato."}
                  </p>
                  {tab === "miei" && (
                    <Button
                      size="sm"
                      className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                      onClick={() => { setEditingModello(null); setEditorOpen(true); }}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" />
                      Crea modello
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-2.5 font-medium text-slate-500">Nome modello</th>
                        <th className="text-left px-4 py-2.5 font-medium text-slate-500">Tipo</th>
                        <th className="text-left px-4 py-2.5 font-medium text-slate-500 hidden md:table-cell">Descrizione</th>
                        <th className="text-left px-4 py-2.5 font-medium text-slate-500 hidden lg:table-cell">Ultimo aggiornamento</th>
                        <th className="text-left px-4 py-2.5 font-medium text-slate-500">Stato</th>
                        <th className="text-right px-4 py-2.5 font-medium text-slate-500">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modelli.map((m) => (
                        <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${TIPO_MODELLO_COLORS[m.tipo] || "bg-slate-100"}`}>
                                <FileText className="w-3.5 h-3.5" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{m.nome}</p>
                                {m.is_base && (
                                  <span className="text-[10px] text-slate-400">Modello base</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`text-[10px] px-2 py-0.5 border-0 ${TIPO_MODELLO_COLORS[m.tipo] || "bg-slate-100 text-slate-600"}`}>
                              {TIPO_MODELLO_LABELS[m.tipo] || m.tipo}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-500 hidden md:table-cell max-w-xs truncate">
                            {m.descrizione || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-400 hidden lg:table-cell">
                            {m.updated_at
                              ? new Date(m.updated_at).toLocaleString("it-IT", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              className={`text-[10px] px-2 py-0.5 border-0 ${
                                m.visibilita === "pubblico"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {m.visibilita === "pubblico" ? "Pubblico" : "Privato"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <MoreVertical className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="text-xs w-44">
                                <DropdownMenuItem onClick={() => { setEditingModello(m); setEditorOpen(true); }}>
                                  <Eye className="w-3.5 h-3.5 mr-2" />
                                  Visualizza / modifica
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setEditingModello(m); setEditorOpen(true); }}>
                                  <Pencil className="w-3.5 h-3.5 mr-2" />
                                  Modifica
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplica(m)}>
                                  <Copy className="w-3.5 h-3.5 mr-2" />
                                  Duplica
                                </DropdownMenuItem>
                                {!m.is_base && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => setDeleteTarget(m)}
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                                      Elimina
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Paginazione */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-xs text-slate-500">
                  <span>{total} modelli totali</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </Button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                      <Button
                        key={p}
                        variant={p === page ? "default" : "outline"}
                        size="sm"
                        className={`h-7 w-7 text-xs ${p === page ? "bg-[#4CAF50] hover:bg-[#388E3C]" : ""}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      ›
                    </Button>
                  </div>
                  <span>Righe per pagina: {perPage}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {!embedded && (
        <div className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
          <InfoCard icon={Cloud} iconColor="bg-blue-500" title="Modelli Fatture in Cloud">
            Questi modelli permettono di creare fatture utilizzando i template definiti
            direttamente in Fatture in Cloud. Clicca "Sincronizza modelli" per importarli.
          </InfoCard>
          <InfoCard icon={Layout} iconColor="bg-violet-500" title="Modelli personalizzati">
            Crea e gestisci i tuoi modelli personalizzati, precompilati con i dati ospite,
            periodo e retta come base di partenza.
          </InfoCard>
          <InfoCard icon={Sparkles} iconColor="bg-amber-500" title="Variabili dinamiche">
            I modelli possono usare variabili come{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px]">{"{{ospite.nome}}"}</code>,{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px]">{"{{retta.importo}}"}</code>,{" "}
            <code className="bg-slate-100 px-1 rounded text-[10px]">{"{{periodo.mese_anno}}"}</code>{" "}
            e condizioni per includere/escludere righe.
          </InfoCard>
        </div>
        )}
      </div>

      {/* Editor modello */}
      <ModelloFatturaEditor
        open={editorOpen}
        modello={editingModello}
        onClose={() => { setEditorOpen(false); setEditingModello(null); }}
        onSave={handleEditorSave}
      />

      {/* Dialog conferma eliminazione */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina modello</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il modello "{deleteTarget?.nome}"? L'operazione non può
              essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
